import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

export const inter = '"Inter", sans-serif';

const baseButtonSx = {
  height: 40,
  borderRadius: "8px",
  fontFamily: "Inter",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "18px",
  textTransform: "none",
  px: 2,
  whiteSpace: "nowrap",
  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
};

export const primaryButtonSx = {
  ...baseButtonSx,
  background: "#148DFD",
  color: "#FFF",
  "&:hover": { background: "#0077CC" },
  "&.Mui-disabled": { background: "#F9FAFA", color: "#93A1B0", border: "1px solid #D5DCE1" },
};

export const secondaryButtonSx = {
  ...baseButtonSx,
  border: "1px solid #D5DCE1",
  background: "#FFF",
  color: "#405261",
  "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
};

export const dangerOutlineButtonSx = {
  ...secondaryButtonSx,
  color: "#FF4D4F",
};

export const dangerButtonSx = {
  ...baseButtonSx,
  background: "#FF4D4F",
  color: "#FFF",
  boxShadow: "none",
  "&:hover": { background: "#E63E40" },
};

export const cardSx = {
  borderRadius: "8px",
  border: "1px solid #E5E9EC",
  background: "#FFF",
};

export const menuPaperSx = {
  width: 200,
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  background: "#FFF",
  boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
  mt: "4px",
  py: "8px",
  "& .MuiMenuItem-root": {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 400,
    color: "#405261",
    px: "16px",
    "&:hover": { background: "#F9FAFA" },
  },
};

export const labelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
};

export const valueSx = {
  color: "#061522",
  fontFamily: "Inter",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "18px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const formatNumber = (n) => Number(n || 0).toLocaleString("en-US");
export const formatCurrency = (n) => `$${formatNumber(n)}`;

export function SectionCard({ title, action, children, sx }) {
  return (
    <Box sx={{ ...cardSx, p: "20px", ...sx }}>
      {(title || action) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 16, fontWeight: 500, lineHeight: "20px" }}>
            {title}
          </Typography>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

export function InfoField({ label, value, mono = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ ...labelSx, mb: 0.75 }}>{label}</Typography>
      <Typography
        sx={{ ...valueSx, ...(mono ? { fontFamily: '"Apercu Mono Pro", monospace', fontSize: 13 } : {}) }}
        title={typeof value === "string" ? value : undefined}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export function Chip({ label }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 28,
        px: 1.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
        color: "#405261",
        fontFamily: "Inter",
        fontSize: 12,
        lineHeight: "18px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

const PLAN_COLORS = {
  Free: { color: "#405261", bg: "#F9FAFA", border: "#D5DCE1" },
  Starter: { color: "#08979C", bg: "#E6FFFB", border: "#87E8DE" },
  Team: { color: "#148DFD", bg: "#E6F4FF", border: "#91CAFF" },
  Business: { color: "#722ED1", bg: "#F9F0FF", border: "#D3ADF7" },
  Enterprise: { color: "#D46B08", bg: "#FFF7E6", border: "#FFD591" },
};

export function PlanPill({ plan }) {
  const c = PLAN_COLORS[plan] || PLAN_COLORS.Free;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 24,
        px: "10px",
        borderRadius: "6px",
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.color,
        fontFamily: "Inter",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {plan}
    </Box>
  );
}

export function UsageBar({ value, max, height = 6 }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const color = pct >= 90 ? "#FF4D4F" : pct >= 75 ? "#FAAD14" : "#148DFD";
  return (
    <Box sx={{ width: "100%", height, borderRadius: 100, background: "#EEF2F6", overflow: "hidden" }}>
      <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 100, background: color }} />
    </Box>
  );
}

export function HealthScore({ score }) {
  const color = score >= 80 ? "#52C41A" : score >= 50 ? "#FAAD14" : "#FF4D4F";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
      <Box sx={{ flex: 1, maxWidth: 56, height: 6, borderRadius: 100, background: "#EEF2F6", overflow: "hidden" }}>
        <Box sx={{ width: `${score}%`, height: "100%", background: color, borderRadius: 100 }} />
      </Box>
      <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 13 }}>{score}</Typography>
    </Box>
  );
}

export function PlatformTabs({ tabs, active, onChange }) {
  return (
    <div className="platform-admin-tabs-row">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`platform-admin-tab-item${active === t.key ? " active" : ""}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {typeof t.count === "number" ? ` (${t.count})` : ""}
        </button>
      ))}
    </div>
  );
}
